import React, { Component, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Always log so production issues are diagnosable.
    // (UI still hides details unless debug is enabled.)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Stash details for E2E/debug and send a lightweight analytic in prod so we can triage
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__E2E_LAST_REACT_ERROR__ = {
        name: error.name,
        message: error.message,
        stack: error.stack || null,
        componentStack: errorInfo.componentStack || null,
      };
    } catch {}

    try {
      // Send a small analytics payload even in production so we can triangulate crashes.
      // Avoid awaiting or letting errors bubble.
      const payload = {
        name: error.name,
        message: String(error.message).slice(0, 200),
        ua: (typeof navigator !== 'undefined' ? (navigator.userAgent || '').slice(0, 200) : ''),
        url: (typeof window !== 'undefined' ? window.location.href : ''),
        componentStack: (errorInfo && errorInfo.componentStack ? errorInfo.componentStack.slice(0, 1000) : null),
      };
      import('@/lib/analytics').then((m) => {
        try { (m.default as any).trackEvent('REACT_BOUNDARY_ERROR', payload); } catch {}
      }).catch(() => {});
    } catch {}

  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-900/20 via-background to-orange-900/20 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                The game encountered an unexpected error. Do not worry, your progress is safe!
              </p>
              
              {this.state.error && (
                <div className="bg-muted p-3 rounded-md">
                  <div className="text-xs font-mono text-destructive break-words space-y-1">
                    {(this.state.error.stack || this.state.error.message).split('\n').map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                  {(() => {
                    try {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const info = (window as any).__E2E_LAST_WINDOW_ERROR__ as
                        | { type?: string; message?: string; filename?: string; lineno?: number; colno?: number }
                        | undefined;
                      if (!info) return null;
                      return (
                        <div className="mt-3 border-t border-border/50 pt-3">
                          <div className="text-[11px] font-mono text-muted-foreground break-words space-y-1">
                            <div>{`window.${info.type || 'error'}: ${info.message || ''}`}</div>
                            {info.filename && info.lineno ? (
                              <div>{`${info.filename}:${info.lineno}${info.colno ? `:${info.colno}` : ''}`}</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                  {this.state.errorInfo?.componentStack && (
                    <div className="mt-3 border-t border-border/50 pt-3">
                      <div className="text-[11px] font-mono text-muted-foreground break-words space-y-1">
                        {this.state.errorInfo.componentStack
                          .split('\n')
                          .map((line, idx) => (line.trim().length ? <div key={idx}>{line}</div> : null))}
                      </div>
                    </div>
                  )}

                  {/* Debug UI: only shown when ?debug=1 is present to avoid exposing internals in normal prod */}
                  {(() => {
                    try {
                      const debugOn = (typeof window !== 'undefined') && (window.location.search.includes('debug=1') || window.location.hash.includes('debug=1'));
                      if (!debugOn) return null;
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      const errObj = (window as any).__E2E_LAST_REACT_ERROR__;
                      if (!errObj) return (
                        <div className="mt-3 border-t border-border/50 pt-3">
                          <div className="text-sm text-muted-foreground">Debug: no saved react error object found on window (set ?debug=1 and reproduce the error)</div>
                        </div>
                      );

                      const pretty = JSON.stringify(errObj, null, 2);

                      return (
                        <div className="mt-3 border-t border-border/50 pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-medium">Debug: saved react error object</div>
                            <div className="flex gap-2">
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => {
                                  try { navigator.clipboard.writeText(pretty); alert('Copied debug JSON to clipboard'); } catch (e) { try { console.log('debug payload', pretty); alert('Could not copy to clipboard; see console'); } catch {} }
                                }}
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                          <pre className="text-[11px] font-mono text-muted-foreground overflow-auto max-h-64 whitespace-pre-wrap bg-muted p-2 rounded">{pretty}</pre>
                        </div>
                      );
                    } catch (e) {
                      return null;
                    }
                  })()}
                </div>
              )}

              <div className="flex gap-2">
                <Button 
                  onClick={this.handleReset}
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
                <Button 
                  onClick={() => window.location.href = '#/dashboard'}
                  variant="outline"
                  className="flex-1"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
